import {
    Card,
    CardContent,
    CardHeader, FormControl,
    Grid,
    IconButton, Input, InputAdornment,
    Paper, Table,
    TableBody,
    TableCell,
    TableContainer, TableFooter,
    TableHead, TableSortLabel, ToggleButton, ToggleButtonGroup, Tooltip
} from "@mui/material";
import theme2 from "../../../lib/theme";
import * as React from "react";
import CachedIcon from "@mui/icons-material/Cached";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Loading from "../../../components/loading";
import TableHeaderRow from "../../../components/TableHeaderRow";
import StripedTableRow from "../../../components/StripedTableRow";
import HttpsIcon from "@mui/icons-material/Https";
import TableFooterRow from "../../../components/TableFooterRow";
import moment from "moment";

const defaultSummary = {count: 0};
export default function Unknowns(params: {dateRange: any, reload: any, verticals: string[]}) {
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState([] as any[]);
    const [search, setSearch] = React.useState('');
    const [filtered, setFiltered] = React.useState([] as any[]);
    const [summary, setSummary] = React.useState({...defaultSummary} as any);
    const [sort, setSort] = React.useState({column: 'count', sortAsc: false});

    const reload = async () => {
        setLoading(true);

        // do a post to https://membertool.banneredgemedia.com/integration/publishers

        const s = moment.tz(moment(params.dateRange[0]).format("YYYY-MM-DD"), "America/Los_Angeles").format();
        let e = moment(params.dateRange[1]).format();
        let dash = e.lastIndexOf("-");
        e = e.substring(0, dash);
        e = moment.tz(e, "America/Los_Angeles").format();

        const response = await fetch('https://membertool.banneredgemedia.com/integration/unknowns', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                startDate: s,
                endDate: e,
                verticals: params.verticals
            })
        });

        const json = await response.json();
        setData(json);
        calcFiltered(json, search, sort);

        setLoading(false);
    }


    React.useEffect(() => {
        if (params.reload) {
            reload().then(() => {});
        }
    }, [params.reload]);

    const calcFiltered = (_data: any[], _search: string, _sort: any) => {
        let dat = _data.filter((d: any) => !_search.trim() || d.campaign.toLowerCase().includes(_search.trim().toLowerCase()));

        dat = dat.sort((a: any, b: any) => {
            if (_sort.sortAsc === false) {
                if (a[_sort.column] < b[_sort.column]) {
                    return 1;
                } else {
                    return -1;
                }
            } else {
                if (a[_sort.column] > b[_sort.column]) {
                    return 1;
                } else {
                    return -1;
                }
            }
        });

        setFiltered(dat);

        let sum = {...defaultSummary};
        dat.forEach((d: any) => {
            sum.count += d.count;
        });
        setSummary(sum);
    }

    const doSort = (column: string, defaultAsc: boolean = false) => {
        let s;
        if (sort.column === column) {
            s = {column: column, sortAsc: !sort.sortAsc};
        } else {
            s = {column: column, sortAsc: defaultAsc};
        }
        setSort(s);

        calcFiltered(data, search, s);
    }

    return (
        <Card sx={{height: '463px', maxWidth: 'calc(100vw - 10px)'}}>
            <CardHeader title={"Unknowns"} sx={{backgroundColor: theme2.palette.primary.main, color: theme2.palette.primary.contrastText, padding: '5px'}}
                        titleTypographyProps={{sx:{fontSize: '18px'}}}
                        action={
                <>
                    <IconButton aria-label="refresh" sx={{color: theme2.palette.primary.contrastText}} onClick={reload}>
                        <CachedIcon />
                    </IconButton>
                </>
                        }
            />
            <CardContent>
                {loading ? <Box sx={{marginTop: '130px'}}><Loading/></Box> : <>
                    <FormControl variant="outlined" sx={{ m: 1, maxWidth: 190 }} size="small">
                        <Input
                            id="search"
                            type="text"
                            spellCheck="false"
                            placeholder="Search"
                            value={search || ''}
                            onChange={(e: any) => {setSearch(e.target.value); calcFiltered(data, e.target.value, sort)}}
                            startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>}
                        />
                    </FormControl>

                    <TableContainer component={Paper} sx={{maxWidth: 'calc(100vw - 40px)', overflowY: 'auto', maxHeight: '333px'}}>
                        <Table size="small"
                               sx={{
                                   "& .MuiTableRow-root:hover": {
                                       backgroundColor: "primary.light"
                                   }
                               }}
                               stickyHeader={true}
                        >
                            <TableHead>
                                <TableHeaderRow>
                                    <TableCell>
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'campaign'}
                                            direction={sort.column === 'campaign' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('campaign', true)}}
                                        >
                                            Campaign
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'status'}
                                            direction={sort.column === 'status' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('status', false)}}
                                        >
                                            Status
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'count'}
                                            direction={sort.column === 'count' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('count', false)}}
                                        >
                                            Count
                                        </TableSortLabel>
                                    </TableCell>
                                </TableHeaderRow>
                            </TableHead>
                            <TableBody>
                                {filtered.map((row: any) => (
                                    <StripedTableRow key={row.publisher_id}>
                                        <TableCell sx={{minWidth: '200px'}}>{row.campaign}</TableCell>
                                        <TableCell sx={{minWidth: '100px', backgroundColor: theme2.palette.error.light}}>{row.status}</TableCell>
                                        <TableCell align="right">{row.count.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}</TableCell>
                                    </StripedTableRow>
                                ))}
                                </TableBody>
                            <TableFooter>
                                <TableFooterRow>
                                    <TableCell>Totals</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right">{summary.count.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}</TableCell>
                                </TableFooterRow>
                            </TableFooter>
                        </Table>

                    </TableContainer>
                </>}
            </CardContent>
        </Card>
    );
}
